import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PDFPageProxy } from 'pdfjs-dist';
import { Spinner } from '@Components/Spinner';
import './PdfPreview.scss';

interface PdfPreviewProps {
  pdf: Blob;
  title: string;
}

// pdf.js is ~1MB, so load it only when a preview actually renders.
const loadPdfJs = async () => {
  const [pdfjs, worker] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url')
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs;
};

// Draws the first page of a PDF onto a canvas scaled to fit inside the container, so the whole page
// is visible with no scrollbars or browser viewer chrome.
export const PdfPreview = ({ pdf, title }: PdfPreviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [page, setPage] = useState<PDFPageProxy>();
  const [rendered, setRendered] = useState(false);
  const [failed, setFailed] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Parse the PDF once per blob; resizing only redraws the canvas below.
  useEffect(() => {
    let cancelled = false;
    let destroy = () => {};
    setPage(undefined);
    setRendered(false);
    setFailed(false);
    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        const data = await pdf.arrayBuffer();
        // Cleanup may already have run while the awaits above were pending; don't start a task
        // that nothing would ever destroy.
        if (cancelled) return;
        const task = pdfjs.getDocument({ data });
        destroy = () => task.destroy();
        const doc = await task.promise;
        const firstPage = await doc.getPage(1);
        if (!cancelled) {
          setPage(firstPage);
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
      destroy();
    };
  }, [pdf]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !page || size.width === 0 || size.height === 0) return undefined;
    const unscaled = page.getViewport({ scale: 1 });
    const scale = Math.min(size.width / unscaled.width, size.height / unscaled.height);
    const viewport = page.getViewport({ scale });
    const pixelRatio = window.devicePixelRatio || 1;
    canvas.width = Math.floor(viewport.width * pixelRatio);
    canvas.height = Math.floor(viewport.height * pixelRatio);
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;
    const task = page.render({
      canvas,
      viewport,
      transform: pixelRatio === 1 ? undefined : [pixelRatio, 0, 0, pixelRatio, 0, 0]
    });
    task.promise.then(
      () => setRendered(true),
      e => {
        if (e?.name !== 'RenderingCancelledException') {
          setFailed(true);
        }
      }
    );
    return () => task.cancel();
  }, [page, size]);

  return (
    <div
      className='pdf-preview'
      ref={containerRef}
      role='img'
      aria-label={title}
      data-testid='pdf-preview'
      data-rendered={rendered}
    >
      {failed ? (
        <div className='pdf-preview-message'>Could not display the PDF.</div>
      ) : (
        <>
          {!rendered && (
            <div className='pdf-preview-message'>
              <Spinner />
            </div>
          )}
          <canvas ref={canvasRef} />
        </>
      )}
    </div>
  );
};
